/**
 * 消息通知中心组件
 * - 显示通知列表
 * - 处理邀请
 * - 标记已读
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BellIcon,
  XMarkIcon,
  TrashIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { notificationApi, invitationApi } from '../services/api';
import Toast from './Toast';

interface Notification {
  id: number;
  type: string;
  title: string;
  content?: string;
  is_read: boolean;
  created_at: string;
  sender_name?: string;
  related_id?: string;
  action_url?: string;
  data?: any;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error' | 'warning' | 'info'; message: string }>({
    show: false, type: 'success', message: ''
  });
  const [processingInvite, setProcessingInvite] = useState<number | null>(null);

  const showToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setToast({ show: true, type, message });
  };

  // 加载通知列表
  const loadNotifications = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    try {
      const res = await notificationApi.getList({ limit: 50 });
      const data = Array.isArray(res.data) ? res.data : [];
      setNotifications(data);
    } catch (error) {
      console.error('加载通知失败:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  // 标记单个已读
  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationApi.markSingleAsRead(notificationId);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('标记已读失败:', error);
    }
  };

  // 删除通知
  const handleDelete = async (notificationId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationApi.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      showToast('success', '已删除');
    } catch (error) {
      showToast('error', '删除失败');
    }
  };

  // 处理邀请
  const handleProcessInvite = async (notification: Notification, action: 'accept' | 'reject') => {
    if (!notification.data?.member_id) return;
    
    setProcessingInvite(notification.id);
    try {
      const res = await invitationApi.process(notification.data.member_id, action);
      showToast('success', res.data?.message || (action === 'accept' ? '已接受邀请' : '已拒绝邀请'));
      
      // 标记通知为已读并更新状态
      setNotifications(prev => prev.map(n => 
        n.id === notification.id ? { 
          ...n, 
          is_read: true,
          data: { ...n.data, status: action === 'accept' ? 'accepted' : 'rejected' }
        } : n
      ));
    } catch (error: any) {
      showToast('error', error?.response?.data?.detail || '处理邀请失败');
    } finally {
      setProcessingInvite(null);
    }
  };

  // 复制邀请链接
  const handleCopyInviteLink = (inviteCode: string) => {
    const link = `${window.location.origin}/invite?code=${inviteCode}`;
    navigator.clipboard.writeText(link);
    showToast('success', '邀请链接已复制');
  };

  // 格式化时间
  const formatTime = (timeStr: string) => {
    const date = new Date(timeStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    if (diff < 60000) return '刚刚';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
    return date.toLocaleDateString();
  };

  // 获取通知图标
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'group_invite':
        return <UserGroupIcon className="w-5 h-5 text-blue-500" />;
      case 'group_accept':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'group_reject':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <EnvelopeIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  // 判断邀请是否已处理
  const isInviteProcessed = (notification: Notification) => {
    // 直接用 is_read 判断，后端已标记为已读
    return notification.is_read;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 遮罩层 */}
      <div 
        className="fixed inset-0 z-40 bg-black/30" 
        onClick={onClose}
      />
      
      {/* 弹窗 */}
      <div className="fixed top-16 right-4 z-50 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl flex flex-col max-h-[70vh]">
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-base font-semibold text-gray-900">消息通知</h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* 消息列表 */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <BellIcon className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-sm">暂无消息</p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {notifications.map(notification => {
                const processed = isInviteProcessed(notification);
                const isInvite = notification.type === 'group_invite';
                
                return (
                  <div
                    key={notification.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      processed 
                        ? 'bg-gray-50 border-gray-200' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    <div className="flex gap-3">
                      {/* 图标 */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification.type)}
                      </div>

                      {/* 内容 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className={`text-sm font-medium truncate ${
                            processed ? 'text-gray-400' : 'text-gray-900'
                          }`}>
                            {notification.title}
                          </h4>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        
                        {notification.content && (
                          <p className={`text-xs mt-1 line-clamp-2 ${
                            processed ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {notification.content}
                          </p>
                        )}

                        {/* 邀请操作按钮 */}
                        {isInvite && !processed && (
                          <div className="flex items-center gap-2 mt-3">
                            <button
                              onClick={() => handleProcessInvite(notification, 'accept')}
                              disabled={processingInvite === notification.id}
                              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              接受
                            </button>
                            <button
                              onClick={() => handleProcessInvite(notification, 'reject')}
                              disabled={processingInvite === notification.id}
                              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50"
                            >
                              拒绝
                            </button>
                          </div>
                        )}

                        {/* 已处理的邀请状态 */}
                        {isInvite && processed && (
                          <div className="mt-2 text-xs text-gray-400">
                            {notification.data?.status === 'accepted' ? '已接受' : 
                             notification.data?.status === 'rejected' ? '已拒绝' : '已处理'}
                          </div>
                        )}
                      </div>

                      {/* 删除按钮 */}
                      <button
                        onClick={(e) => handleDelete(notification.id, e)}
                        className="flex-shrink-0 p-1 text-gray-300 hover:text-red-500 rounded"
                        title="删除"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          show={toast.show}
          type={toast.type}
          title={toast.message}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
        />
      )}
    </>
  );
};

export default NotificationCenter;
